import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  Input,
  Heading,
  Text,
} from '@chakra-ui/react';
import './ChangePasswordForm.scss';
import AuthSuccessMessage from '../AuthSuccessMessage/AuthSuccessMessage.tsx';
import { useTranslation } from 'react-i18next';
import AuthService from '../../services/AuthService.ts';
import { useParams } from 'react-router-dom';

interface ChangePasswordFormProps {
  onBackToLoginClick: () => void;
  isTokenValid: boolean;
}

const ChangePasswordForm: React.FC<ChangePasswordFormProps> = ({
                                                                 onBackToLoginClick,
                                                                 isTokenValid,
                                                               }) => {
  const { t } = useTranslation();
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordsMatch, setPasswordsMatch] = useState(true);
  const { token } = useParams<{ token: string }>();

  useEffect(() => {
    if (newPassword.trim() !== '' || confirmPassword.trim() !== '') {
      setIsButtonDisabled(false);
    } else {
      setIsButtonDisabled(true);
    }
  }, [newPassword, confirmPassword]);

  const handleChangePasswordClick = async (): Promise<void> => {
    if (newPassword !== confirmPassword) {
      setPasswordsMatch(false);
      return;
    }

    setIsSubmitting(true);

    try {
      await AuthService.resetPassword(token!, newPassword, confirmPassword);
      setShowSuccessMessage(true);
    } catch (error) {
      console.error('Error in handleChangePasswordClick:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isTokenValid) {
    return (
      <Box textAlign="center" className="login-form">
        <Heading as="h1" size="md" fontWeight={500} mb={4}>
          {t('invalidOrExpiredToken')}
        </Heading>
        <Button onClick={onBackToLoginClick}>{t('backToLogin')}</Button>
      </Box>
    );
  }

  if (showSuccessMessage) {
    return (
      <AuthSuccessMessage
        message={t('passwordResetSuccess')}
        onBackToLoginClick={onBackToLoginClick}
      />
    );
  }

  return (
    <Box textAlign="center" className="login-form">
      <Heading
        as="h1"
        size={{ base: 'md', md: 'lg' }}
        fontWeight={500}
        className="auth-title"
      >
        {t('changePassword_Header')}
      </Heading>

      <FormControl id="new-password" mb={{ base: 5, md: 10 }}>
        <Input
          variant="outline-only-bottom"
          color="black"
          type="password"
          placeholder={t('newPassword')}
          value={newPassword}
          onChange={(e) => {
            setNewPassword(e.target.value);
          }}
        />
      </FormControl>

      <FormControl id="confirm-password" mb={2}>
        <Input
          variant="outline-only-bottom"
          color="black"
          type="password"
          placeholder={t('confirmPassword')}
          value={confirmPassword}
          onChange={(e) => {
            setConfirmPassword(e.target.value);
          }}
        />
      </FormControl>

      {!passwordsMatch && (
        <Box position="relative">
          <Text
            color="red.500"
            mb={4}
            fontSize="small"
            textAlign="right"
            position="absolute"
            right="0"
          >
            {t('passwordMismatch')}
          </Text>
        </Box>
      )}

      <Button
        colorScheme="alea"
        variant="solid"
        borderRadius={20}
        type="submit"
        mb={4}
        width={{ base: '100%', md: '300px' }}
        height={{ base: '45px', md: '50px' }}
        fontSize={{ base: '14px', md: '18px' }}
        fontWeight={400}
        isDisabled={isButtonDisabled}
        isLoading={isSubmitting}
        mt={{ base: '41px', md: '71px' }}
        onClick={handleChangePasswordClick}
      >
        {t('changePassword_Button')}
      </Button>
    </Box>
  );
};

export default ChangePasswordForm;